import { escapeHtml } from '../format.js';
import { groupBySection, packColumns, type MenuBlock, type MenuColumn } from '../paginate.js';
import type { MenuBoardContent, MenuItemContent, MenuTheme } from '../types.js';
import {
  ARABIC_LINE,
  bilingual,
  fitScale,
  hasTitle,
  image,
  listColumns,
  listCss,
  listItem,
  sectionSubtitle,
  sectionTitle,
  type MenuFamily,
  type MenuStyleVariant,
  type MenuTemplate,
  type RenderContext,
} from './common.js';

/**
 * Walnut, layout A: a dark wood board in three columns. Each column has a dish photo at the top
 * and another at the bottom, and between them its sections under bright tab headings whose rule
 * runs on across the column; names in white with the prices in the accent colour, no leaders.
 *
 * The look (wood, tabs, rows, photos, header) is shared with walnut-wall and walnut-cards.
 */

/* ------------------------------------------------------------------ shared look */

const svg = (markup: string) => `url("data:image/svg+xml,${encodeURIComponent(markup)}")`;

/** Wood grain: long streaks of light and dark, stretched along the planks. */
const GRAIN = svg(
  "<svg xmlns='http://www.w3.org/2000/svg' width='700' height='700'>" +
    "<filter id='d' x='0' y='0' width='100%' height='100%'><feTurbulence type='fractalNoise' baseFrequency='.035 .003' numOctaves='4' seed='3' stitchTiles='stitch'/>" +
    "<feColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 .6 0 0 0 -.24'/></filter>" +
    "<filter id='l' x='0' y='0' width='100%' height='100%'><feTurbulence type='fractalNoise' baseFrequency='.08 .004' numOctaves='3' seed='8' stitchTiles='stitch'/>" +
    "<feColorMatrix values='0 0 0 0 1 0 0 0 0 .85 0 0 0 0 .65 .3 0 0 0 -.17'/></filter>" +
    "<rect width='700' height='700' filter='url(#d)'/><rect width='700' height='700' filter='url(#l)'/></svg>",
);

export const WN_FAMILY: Omit<MenuFamily, 'layout'> = { id: 'walnut', name: 'Walnut' };

export const WN_THEME: MenuTheme = {
  backgroundColor: '#3a2618',
  surfaceColor: '#4a3121',
  textColor: '#f6eee5',
  primaryColor: '#ffffff',
  accentColor: '#f7941d',
  fontId: 'modern',
  backgroundMediaId: null,
};

export const WN_VARIANTS: MenuStyleVariant[] = [
  { id: 'walnut', name: 'Walnut & orange', theme: {} },
  { id: 'espresso', name: 'Espresso & gold', theme: { backgroundColor: '#24170f', surfaceColor: '#332217', accentColor: '#f5be2b' } },
  { id: 'oak', name: 'Oak & red', theme: { backgroundColor: '#5a3b22', surfaceColor: '#6a4529', accentColor: '#e5432d' } },
  { id: 'ash', name: 'Grey ash & lime', theme: { backgroundColor: '#3a3936', surfaceColor: '#4a4844', accentColor: '#9ccc3c' } },
];

/** Wood planks with grain and a soft vignette on a design's root. */
export function wnRootCss(S: string): string {
  return `
${S}{background-color:var(--mb-bg);
  background-image:radial-gradient(ellipse at 50% 45%,transparent 45%,rgba(0,0,0,.45)),
    repeating-linear-gradient(90deg,rgba(0,0,0,.32) 0 .14em,rgba(255,255,255,.05) .14em .26em,transparent .26em 17.5em),${GRAIN};
  background-size:100% 100%,100% 100%,60em 60em}
${S}.mb-has-bg{background-image:none}`;
}

/** Tab heading: the section's name on an accent tab, its rule running on to the column's edge. */
export function wnTab(ctx: RenderContext, b: MenuBlock): string {
  if (!hasTitle(b.section)) return '';
  const sub = b.continued ? '' : sectionSubtitle(ctx, b.section, 'wn-sub');
  return `<div class="wn-head"><div class="wn-tabrow"><div class="wn-tab">${sectionTitle(ctx, b.section, 'wn-tt')}</div><i class="wn-rule"></i></div>${sub}</div>`;
}

/** A section: tab heading and rows. */
export function wnSection(ctx: RenderContext, b: MenuBlock): string {
  return `<div class="wn-sec">${wnTab(ctx, b)}${b.items.map((i) => listItem(ctx, i, 'wn', false)).join('')}</div>`;
}

export const WN_ITEM = { nameEm: 1.35, descEm: 0.9, perLine: 40, gap: 0.8 };

export function wnRowsCss(S: string): string {
  return `
${S} .wn-sec{margin-bottom:1.2em}
${S} .wn-head{margin-bottom:1.1em}
${S} .wn-tabrow{display:flex;align-items:center}
${S} .wn-tab{flex:0 1 auto;min-width:0;max-width:85%;background:var(--mb-accent);color:#fff;padding:.35em 1.1em;border-radius:.25em;
  box-shadow:0 .3em .7em rgba(0,0,0,.45)}
${S} .wn-tt{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.55em;line-height:1.3;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${S} .wn-tt.mb-2nd{font-size:1.1em;font-weight:700}
${S} .wn-rule{flex:1 1 2em;height:.22em;background:var(--mb-accent);border-radius:.1em}
${S} .wn-sub{font-family:var(--mb-font-script);color:var(--mb-accent);font-size:1.25em;margin-top:.25em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${listCss(S, 'wn', { name: '1.35em', price: '1.35em', desc: '.9em', leader: '0' })}
${S} .wn-item{margin-bottom:.8em}
${S} .wn-line{align-items:baseline}
${S} .wn-name{flex:1 1 auto;font-weight:600;color:var(--mb-primary);-webkit-line-clamp:1}
${S} .wn-price{font-weight:800;color:var(--mb-accent)}
${S} .wn-size small{color:var(--mb-text)}
${S} .wn-badge{color:#fff}
${S} .wn-name2{font-size:1.1em;font-weight:600;line-height:1.3;color:var(--mb-primary);opacity:.8;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${S} .wn-desc{opacity:.7}
`;
}

/** Sections in `count` columns of rows under tab headings. */
export function wnColumns(board: MenuBoardContent, count: number, capacity: number, perLine = WN_ITEM.perLine): MenuColumn[] {
  return listColumns(board, {
    count,
    capacity,
    // Tab and rule; listColumns adds the Arabic line and subtitle.
    heading: 1.55 * 1.3 + 0.7 + 1.1,
    item: { ...WN_ITEM, perLine },
  });
}

/** A photo filling its box, with a soft shadow. */
export function wnPhoto(ctx: RenderContext, mediaId: string | null | undefined, cls: string): string {
  const img = image(ctx, mediaId, 'wn-img');
  return img ? `<div class="wn-photo ${cls}">${img}</div>` : `<div class="wn-photo wn-nophoto ${cls}"></div>`;
}

export function wnPhotoCss(S: string): string {
  return `
${S} .wn-photo{position:relative;overflow:hidden;border-radius:.3em;background:var(--mb-surface);box-shadow:0 .6em 1.4em rgba(0,0,0,.55)}
${S} .wn-img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover}
${S} .wn-nophoto{background:linear-gradient(135deg,var(--mb-surface),color-mix(in srgb,var(--mb-surface) 60%,#000))}`;
}

/**
 * Photos for the spots of one page: dishes on the page first (in order), then the board's other
 * dishes, so every spot is filled when the board has photos at all.
 */
export function wnPhotoIds(ctx: RenderContext, count: number): (string | null)[] {
  const onPage = ctx.page.columns.flat().flatMap((b) => b.items).map((i) => i.imageMediaId);
  const all = [...onPage, ...ctx.board.items.map((i) => i.imageMediaId)].filter((id): id is string => Boolean(id));
  const unique = [...new Set(all)];
  if (!unique.length) return Array.from({ length: count }, () => null);
  return Array.from({ length: count }, (_, i) => unique[i % unique.length]!);
}

export function wnHasPhotos(board: MenuBoardContent): boolean {
  return board.items.some((i) => i.imageMediaId);
}

/** Script brand, big title, tagline. */
export function wnHeader(ctx: RenderContext, cls: string, maxChars: number): string {
  const { board } = ctx;
  const title = board.settings.title ?? board.name;
  return [
    `<div class="${cls}">`,
    bilingual(ctx, board.brand.name, board.brand.nameAr, `${cls}-brand`),
    `<div style="font-size:${fitScale(title, maxChars, 0.5).toFixed(3)}em">${bilingual(ctx, title, board.settings.titleAr, `${cls}-title`)}</div>`,
    bilingual(ctx, board.brand.tagline, board.brand.taglineAr, `${cls}-tag`),
    '</div>',
  ].join('');
}

export function wnHeaderCss(S: string, cls: string, titleEm: number): string {
  return `
${S} .${cls}{min-width:0}
${S} .${cls}-brand{font-family:var(--mb-font-script);color:var(--mb-accent);font-size:2.1em;line-height:1.3;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${S} .${cls}-brand.mb-2nd{font-size:1.5em}
${S} .${cls}-title{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:${titleEm}em;line-height:1.1;text-transform:uppercase;letter-spacing:.02em;
  color:var(--mb-primary);text-shadow:0 .06em .2em rgba(0,0,0,.5);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${S} .${cls}-title.mb-2nd{font-size:${(titleEm * 0.5).toFixed(2)}em;line-height:1.35;color:var(--mb-accent)}
${S} .${cls}-tag{font-size:1.1em;font-weight:600;letter-spacing:.18em;text-transform:uppercase;opacity:.85;margin-top:.4em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${S} .${cls}-tag.mb-ar{letter-spacing:0}
`;
}

export function wnHeaderHeight(board: MenuBoardContent, titleEm: number): number {
  const both = board.settings.language === 'both';
  const brand = board.brand.name || board.brand.nameAr ? 2.1 * 1.3 + (both && board.brand.nameAr ? 1.5 * 1.3 * ARABIC_LINE : 0) : 0;
  const title = titleEm * 1.1 + (both && board.settings.titleAr ? titleEm * 0.5 * 1.35 * ARABIC_LINE : 0);
  const tag = board.brand.tagline || board.brand.taglineAr ? 0.4 + 1.1 * 1.4 * (both && board.brand.taglineAr ? 2.2 : 1) : 0;
  return brand + title + tag;
}

/** The escaped name of an item in the board's first language, for captions. */
export function wnCaption(ctx: RenderContext, item: MenuItemContent): string {
  const text = !ctx.showEn && item.titleAr?.trim() ? item.titleAr.trim() : item.title;
  return escapeHtml(text);
}

/* ------------------------------------------------------------------ layout A */

const S = '.mb-t-walnut-columns';
const TOP = 14;
const BOTTOM = 12;
/** Portrait draws the two columns larger, so they fill the tall screen. */
const PORTRAIT_SCALE = 1.25;

function columns(board: MenuBoardContent, portrait: boolean): MenuColumn[] {
  const photos = wnHasPhotos(board) ? TOP + BOTTOM + 2.8 : 0;
  return portrait
    ? wnColumns(board, 2, (120 - 5) / PORTRAIT_SCALE - photos, 30)
    : wnColumns(board, 3, 67.5 - 5 - photos, 34);
}

export const walnutColumns: MenuTemplate = {
  id: 'walnut-columns',
  name: 'Walnut · Three columns',
  description:
    'A dark wood board in three columns, each with a dish photo at the top and the bottom and its sections under bright tab headings, white names and orange prices.',
  defaultTheme: WN_THEME,
  variants: WN_VARIANTS,
  cuisines: ['burgers', 'cafe', 'drinks'],
  family: { ...WN_FAMILY, layout: 'Three columns' },
  uses: { featured: false, sectionImages: false, brand: false, decorations: [] },

  paginate(board, orientation) {
    return packColumns(groupBySection(board), columns(board, orientation === 'portrait'), { balance: true });
  },

  render(ctx) {
    const photos = wnHasPhotos(ctx.board);
    const cols = ctx.page.columns;
    // Each column's own dishes first, then the page's others, so neighbours rarely repeat.
    const spare = wnPhotoIds(ctx, cols.length * 2).filter((id): id is string => Boolean(id));
    const used = new Set<string>();
    const pick = (blocks: MenuBlock[]): (string | null)[] => {
      const own = blocks.flatMap((b) => b.items).map((i) => i.imageMediaId).filter((id): id is string => Boolean(id));
      const order = [...new Set([...own, ...spare])];
      const chosen = [...order.filter((id) => !used.has(id)), ...order].slice(0, 2);
      chosen.forEach((id) => used.add(id));
      return [chosen[0] ?? null, chosen[1] ?? chosen[0] ?? null];
    };
    const body = cols
      .map((blocks) => {
        const [top, bottom] = photos ? pick(blocks) : [null, null];
        return [
          '<div class="wn1-col">',
          photos ? wnPhoto(ctx, top, 'wn1-top') : '',
          `<div class="wn1-list">${blocks.map((b) => wnSection(ctx, b)).join('')}</div>`,
          photos ? wnPhoto(ctx, bottom, 'wn1-bottom') : '',
          '</div>',
        ].join('');
      })
      .join('');
    return `<div class="wn1-grid">${body}</div>`;
  },

  css: `
${wnRootCss(S)}
${S}{padding:2.5em 3em}
${wnRowsCss(S)}
${wnPhotoCss(S)}
${S} .wn1-grid{flex:1;min-height:0;display:grid;grid-template-columns:repeat(3,minmax(0,1fr));column-gap:3.5em}
${S}.mb-portrait .wn1-grid{font-size:${PORTRAIT_SCALE}em;grid-template-columns:repeat(2,minmax(0,1fr));column-gap:2.4em}
${S} .wn1-col{min-width:0;min-height:0;display:flex;flex-direction:column}
${S} .wn1-top{flex:0 0 ${TOP}em;margin-bottom:1.4em}
${S} .wn1-bottom{flex:0 0 ${BOTTOM}em;margin-top:1.4em}
${S} .wn1-list{flex:1 1 auto;min-height:0}
`,
};
